import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot
} from 'recharts';
import './PriceChart.css';

// HMR Update Trigger - CSS soll neu geladen werden
// Custom Tooltip mit korrekter Reihenfolge: Höchstpreis - Durchschnittspreis - Mindestpreis
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    // Erstelle ein Objekt mit den Daten für einfacheren Zugriff
    const data = {};
    payload.forEach(entry => {
      data[entry.dataKey] = entry;
    });

    // Definiere die gewünschte Reihenfolge: Höchstpreis, Durchschnittspreis, Mindestpreis
    const displayOrder = ['maxPrice', 'avgPrice', 'minPrice'];
    const displayed = displayOrder
      .map(key => data[key])
      .filter(item => item); // Filtere undefined aus

    return (
      <div style={{
        backgroundColor: '#1a2d38',
        border: '1px solid #00d4ff',
        borderRadius: '4px',
        padding: '8px 12px',
        color: '#10b981'
      }}>
        <p style={{ margin: '0 0 4px 0', color: '#7dd3fc', fontSize: '12px' }}>
          {label}
        </p>
        {displayed.map((entry, index) => {
          const isMinPrice = entry.dataKey === 'minPrice';
          return (
            <p key={index} style={{ 
              margin: '2px 0', 
              color: entry.color, 
              fontSize: '12px', 
              fontWeight: isMinPrice ? 700 : 600
            }}>
              {entry.name}: <span style={{ color: '#00ff88' }}>€{parseFloat(entry.value).toFixed(2)}</span>
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

export default function PriceChart({ productId }) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // showDetails wird für zukünftige Features benötigt
  // eslint-disable-next-line no-unused-vars
  const [showDetails, setShowDetails] = useState(false);

  const fetchPriceHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`/api/prices/history/${productId}`);
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP Error: ${res.status}`);
      }

      const data = await res.json();
      
      // Wenn keine Daten vorhanden, aber erfolgreich geladen
      if (!Array.isArray(data) || data.length === 0) {
        setChartData([]);
        setError(''); // KEIN Error setzen - das ist normal
        setLoading(false);
        return;
      }
      
      // Formatiere Daten für bessere Lesbarkeit
      const formattedData = data.map(item => ({
        date: new Date(item.date).toLocaleDateString('de-DE', { month: 'short', day: 'numeric' }),
        fullDate: item.date,
        avgPrice: parseFloat(item.avgPrice),
        minPrice: parseFloat(item.minPrice),
        maxPrice: parseFloat(item.maxPrice),
        stores: item.stores
      }));

      setChartData(formattedData);
      setError('');
    } catch (err) {
      console.error('❌ Fehler beim Laden der Preishistorie:', err);
      setChartData([]);
      setError('Fehler beim Laden: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchPriceHistory();
  }, [fetchPriceHistory]);

  if (loading) {
    return <div className="price-chart-container loading" style={{ padding: '12px', background: 'linear-gradient(135deg, #1a3a4a 0%, #0f2a35 100%)', height: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>⏳ Chart wird geladen...</div>;
  }

  if (error) {
    return <div className="price-chart-container error" style={{ padding: '12px', background: 'linear-gradient(135deg, #1a3a4a 0%, #0f2a35 100%)', height: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{error}</div>;
  }

  if (!chartData || chartData.length === 0) {
    return <div className="price-chart-container empty" style={{ padding: '12px', background: 'linear-gradient(135deg, #1a3a4a 0%, #0f2a35 100%)', height: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Keine Preisverlauf-Daten verfügbar</div>;
  }

  // Finde den günstigsten Preis und seinen Index
  const minPricePoint = chartData.reduce((min, current, index) => {
    return current.minPrice < min.price ? { price: current.minPrice, index, date: current.date } : min;
  }, { price: Infinity, index: 0, date: null });

  return (
    <div 
      className="price-chart-container"
      style={{
        padding: '8px',
        background: 'linear-gradient(135deg, #1a3a4a 0%, #0f2a35 100%)',
        overflow: 'hidden',
        height: '100%',
        boxSizing: 'border-box'
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 5, left: -15, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#2a4a5c" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#7dd3fc' }}
            interval={Math.floor(chartData.length / 6)} // Zeige ca. 6 Labels
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#7dd3fc' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="avgPrice"
            stroke="#00d4ff"
            strokeWidth={1}
            strokeDasharray="5 5"
            dot={false}
            activeDot={{ r: 5 }}
            name="Ø Preis"
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="minPrice"
            stroke="#10b981"
            strokeWidth={3}
            dot={false}
            name="Mindestpreis"
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="maxPrice"
            stroke="#ef4444"
            strokeWidth={1}
            strokeDasharray="5 5"
            dot={false}
            name="Höchstpreis"
            isAnimationActive={false}
          />
          
          {/* Markiere den günstigsten Preis */}
          {minPricePoint.index !== undefined && (
            <ReferenceDot
              x={minPricePoint.date}
              y={minPricePoint.price}
              r={6}
              fill="#10b981"
              stroke="#00ff88"
              strokeWidth={2}
            />
          )}
        </LineChart>
      </ResponsiveContainer>

      {showDetails && chartData.length > 0 && (
        <div className="chart-details">
          <div className="chart-stats">
            <div className="stat">
              <span className="stat-label">Durchschnitt:</span>
              <span className="stat-value">€{(
                chartData.reduce((sum, d) => sum + d.avgPrice, 0) / chartData.length
              ).toFixed(2)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Minimum:</span>
              <span className="stat-value">€{Math.min(...chartData.map(d => d.minPrice)).toFixed(2)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Maximum:</span>
              <span className="stat-value">€{Math.max(...chartData.map(d => d.maxPrice)).toFixed(2)}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Trend:</span>
              <span className="stat-value">
                {chartData.length > 1 ? (
                  chartData[chartData.length - 1].avgPrice > chartData[0].avgPrice ? (
                    <span className="trend-up">📈 Steigend</span>
                  ) : chartData[chartData.length - 1].avgPrice < chartData[0].avgPrice ? (
                    <span className="trend-down">📉 Fallend</span>
                  ) : (
                    <span className="trend-stable">➡️ Stabil</span>
                  )
                ) : (
                  'N/A'
                )}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
